import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  ArrowLeft,
  Calendar,
  Mail,
  MoreVertical,
  Pencil,
  Trash2,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import { toast } from "../../common/components/Toast";
import { SPACING } from "../../common/constants/spacing";
import { useProject } from "../../common/contexts/ProjectContext";
import { removeProjectMember } from "../api/members";
import EditMemberModal from "../components/EditMemberModal";

function MemberDetail() {
  const { t } = useTranslation("members");
  const { id } = useParams();
  const navigate = useNavigate();
  const { project, setProject, currentUserRole } = useProject();
  const [member, setMember] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (project?.users && id) {
      const foundMember = project.users.find(
        (user) =>
          user.user_details?.id === parseInt(id) || user.id === parseInt(id),
      );
      setMember(foundMember);
    }
  }, [project, id]);

  const handleBack = () => {
    navigate("/members");
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setShowEditModal(true);
    handleMenuClose();
  };

  const handleRemove = async () => {
    try {
      await removeProjectMember(member.id);
      toast.success(t("messages.removedSuccess"));
      setProject({
        ...project,
        users: project.users.filter((u) => u.id !== member.id),
      });
      navigate("/members");
    } catch (error) {
      toast.error(t("messages.removedError"));
    }
    handleMenuClose();
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "owner":
        return "primary";
      case "producer":
        return "secondary";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  if (!member) {
    return (
      <Box>
        <PageHeader
          title={t("memberDetail.notFound")}
          description={t("memberDetail.notFoundDescription")}
          appendActions={
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={18} />}
              onClick={handleBack}
            >
              {t("memberDetail.backToMembers")}
            </Button>
          }
        />
      </Box>
    );
  }

  const memberName =
    member.user_details?.first_name && member.user_details?.last_name
      ? `${member.user_details.first_name} ${member.user_details.last_name}`
      : member.user_details?.name ||
        member.user_details?.email ||
        "Unknown User";

  return (
    <Box>
      <PageHeader
        title={t("memberDetail.title")}
        description={t("memberDetail.description")}
        appendActions={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={18} />}
              onClick={handleBack}
            >
              {t("memberDetail.backToMembers")}
            </Button>
            {currentUserRole === "owner" && (
              <IconButton onClick={handleMenuOpen}>
                <MoreVertical size={18} />
              </IconButton>
            )}
          </Box>
        }
      />

      <Grid container spacing={3}>
        <Grid xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: "center", p: 4 }}>
              <Avatar
                src={member.user_details?.avatar}
                sx={{
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: SPACING.sm,
                  fontSize: "2rem",
                  fontWeight: 600,
                }}
              >
                {memberName.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h5" gutterBottom>
                {memberName}
              </Typography>
              <Chip
                label={member.role || t("roles.member")}
                color={getRoleColor(member.role)}
                sx={{
                  fontWeight: 500,
                  textTransform: "capitalize",
                  mb: SPACING.sm,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {t("memberDetail.projectMember")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("memberDetail.memberInformation")}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: SPACING.sm,
                    }}
                  >
                    <User
                      size={20}
                      style={{ marginRight: 16, color: "text.secondary" }}
                    />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t("memberDetail.fullName")}
                      </Typography>
                      <Typography variant="body1">{memberName}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: SPACING.sm,
                    }}
                  >
                    <Mail
                      size={20}
                      style={{ marginRight: 16, color: "text.secondary" }}
                    />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t("memberDetail.emailAddress")}
                      </Typography>
                      <Typography variant="body1">
                        {member.user_details?.email || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: SPACING.sm,
                    }}
                  >
                    <User
                      size={20}
                      style={{ marginRight: 16, color: "text.secondary" }}
                    />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t("table.role")}
                      </Typography>
                      <Typography variant="body1">
                        {member.role || t("roles.member")}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: SPACING.sm,
                    }}
                  >
                    <Calendar
                      size={20}
                      style={{ marginRight: 16, color: "text.secondary" }}
                    />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t("memberDetail.joinedDate")}
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(member.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Additional information card */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("memberDetail.projectAccess")}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Typography variant="body2" color="text.secondary" paragraph>
                {member.role?.toLowerCase() === "owner" &&
                  `${t("memberDetail.accessDescriptionOwnerFull")} ${t("memberDetail.accessDescriptionOwnerManage")}`}
                {member.role?.toLowerCase() === "producer" &&
                  `${t("memberDetail.accessDescriptionProducerView")} ${t("memberDetail.accessDescriptionProducerManage")}`}
                {(!member.role || member.role?.toLowerCase() === "member") &&
                  `${t("memberDetail.accessDescriptionMemberBasic")} ${t("memberDetail.accessDescriptionMemberView")}`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEdit}>
          <Pencil size={18} style={{ marginRight: 8 }} />
          {t("memberDetail.editMember")}
        </MenuItem>
        <MenuItem onClick={handleRemove} sx={{ color: "error.main" }}>
          <Trash2 size={18} style={{ marginRight: 8 }} />
          {t("actions.removeFromProject")}
        </MenuItem>
      </Menu>

      {/* Edit Modal */}
      <EditMemberModal
        project={project}
        setProject={setProject}
        showEditMemberModal={showEditModal}
        setShowEditMemberModal={setShowEditModal}
        selectedMember={member}
      />
    </Box>
  );
}

export default MemberDetail;
